import Box from "@mui/material/Box"
import FormControl from "@mui/material/FormControl"
import Grid from "@mui/material/Grid2"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import TextField from "@mui/material/TextField"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"
import { CheckCircleFilledRounded, KeyboardArrowDownOutlined } from "@mui-symbols-material/w300"
import type { ReactNode } from "@tanstack/react-router"
import React, { type FC, forwardRef, useMemo } from "react"
import { Controller, useController, useFormContext } from "react-hook-form"

import { useGetCategoryList } from "../hooks/useGetCategoryList"
import { useGetUnitOfMeasurementList } from "../hooks/useGetUnitOfMeasurementList"

import type { ProductInfoFormValues, ProductSpecificsFormValues } from "./productFormSchema"
import { BLANK_PRODUCT_MANAGER_VALUE } from "./productFormSchema"

import SelectIconWithClear from "@/components/SelectIconWithClearProps/SelectIconWithClear"
import {
  FIELD_ERROR_CLASSES,
  FIELD_LABEL_CLASSES,
  SELECT_STYLING_PROPS,
  TEXT_FIELD_STYLING_PROPS,
} from "@/constants/forms"
import type { Location } from "@/graphql/codegen/graphql"
import { ProductFormInfoItem } from "@/graphql/codegen/graphql"
import { useInfiniteScroll } from "@/hooks/useInfniniteScroll"
import { AddressFormat } from "@/types/common"
import { formatLocationAddress } from "@/utils/utils"

const productFormLabels: Record<keyof typeof ProductFormInfoItem, string> = {
  Bead: "Bead",
  Flake: "Flake",
  Granule: "Granule",
  Liquid: "Liquid",
  LiquidDispersion: "Liquid Dispersion",
  Pellet: "Pellet",
  Powder: "Powder",
}

interface SelectComponentProps {
  name: string
  label: string
  placeholder?: string
  disabled?: boolean
  control: any
  children?: ReactNode
  required?: boolean
  renderValue?: (value: any) => React.ReactNode
  hasClearButton?: boolean
}

export const SelectComponent: FC<SelectComponentProps> = forwardRef((props: SelectComponentProps, ref) => {
  const { name, control, children, label, placeholder, required, hasClearButton, disabled, ...rest } = props
  const {
    field,
    fieldState: { error },
  } = useController({ name, control })

  return (
    <FormControl fullWidth className='mt-6'>
      <label className={FIELD_LABEL_CLASSES}>
        {label}
        {required && <span className='text-red-600'>*</span>}
      </label>
      <Select
        {...field}
        {...SELECT_STYLING_PROPS}
        ref={ref}
        IconComponent={
          hasClearButton
            ? ({ className }) => (
                <SelectIconWithClear value={field.value} onClear={() => field.onChange("")} className={className} />
              )
            : KeyboardArrowDownOutlined
        }
        disabled={disabled}
        value={field.value || ""}
        renderValue={(value) => {
          if (!value) {
            return <span className='text-gray-500'>{placeholder || `Select ${label}`}</span>
          }
          return value
        }}
        {...rest}
      >
        <MenuItem value=''>
          <span className='text-gray-500'>{placeholder || `Select ${label}`}</span>
        </MenuItem>
        {children}
      </Select>
      {error?.message && <p className={FIELD_ERROR_CLASSES}>{error.message}</p>}
    </FormControl>
  )
})

export interface ProductSpecificsFormProps {
  productInfo: ProductInfoFormValues
}

export const ProductSpecificsForm: FC<ProductSpecificsFormProps> = ({ productInfo }) => {
  const { control, setValue, watch } = useFormContext<ProductSpecificsFormValues>()
  const { categoryList, categoryListFetching } = useGetCategoryList()

  const categoryField = watch("category")
  const categoryClassificationsOptions = useMemo(() => {
    return categoryList.find((el) => el.categoryId === categoryField)?.classifications ?? []
  }, [categoryField, categoryList])

  const shouldDisplayClassificationWarningMessage =
    Boolean(categoryField) && !categoryListFetching && !categoryClassificationsOptions.length

  const {
    unitOfMeasurementList,
    unitOfMeasurementListFetching,
    loadMore: loadMoreUnitOfMeasurement,
  } = useGetUnitOfMeasurementList()

  const { containerRef: unitOfMeasurementListRef } = useInfiniteScroll(loadMoreUnitOfMeasurement, {
    isLoading: unitOfMeasurementListFetching,
    hasMore: unitOfMeasurementList.length % 20 === 0,
    threshold: 200,
  })

  const productShipFromValue = productInfo.locationsAssociations.some(({ address1 }) => !!address1)
    ? productInfo.locationsAssociations
        .map((location) => formatLocationAddress(location as Location, AddressFormat.Shipping))
        .join("; ")
    : "Not added"

  return (
    <form className='space-y-6' data-testid='product-specifics-form'>
      <Box className='space-y-4'>
        <Box className='mb-1' display='flex' alignItems='center'>
          <Typography variant='body1' fontWeight={700} className='leading-6 tracking-[0.15px] text-gray-700'>
            1. Product Information
          </Typography>
          <CheckCircleFilledRounded className='ml-1 text-success-500' />
        </Box>

        <Grid container columnSpacing={2} className='ml-4'>
          <Grid size={6}>
            <Typography className='truncate py-0 text-sm font-bold text-gray-900'>Vendor Product SKU</Typography>
          </Grid>
          <Grid size={6}>
            <Typography className='truncate py-0 text-sm text-gray-900'>
              {productInfo.vendorSku || "Not added"}
            </Typography>
          </Grid>

          <Grid size={6}>
            <Typography className='truncate py-0 text-sm font-bold text-gray-900'>Vendor Description</Typography>
          </Grid>
          <Grid size={6}>
            <Typography className='truncate py-0 text-sm text-gray-900'>{productInfo.vendorDescription}</Typography>
          </Grid>

          <Grid size={6}>
            <Typography className='truncate py-0 text-sm font-bold text-gray-900'>Akrochem Description</Typography>
          </Grid>
          <Grid size={6}>
            <Typography className='truncate py-0 text-sm text-gray-900'>
              {productInfo.companyProductDescription}
            </Typography>
          </Grid>

          <Grid size={6}>
            <Typography className='truncate py-0 text-sm font-bold text-gray-900'>Product Ship From</Typography>
          </Grid>
          <Grid size={6}>
            <Tooltip title={productShipFromValue}>
              <Typography className='truncate py-0 text-sm text-gray-900'>{productShipFromValue}</Typography>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      <Typography variant='body1' fontWeight={700} className='leading-6 tracking-[0.15px] text-gray-700'>
        2. Product Specifics
      </Typography>

      <Controller
        name='category'
        control={control}
        render={({ field, fieldState: { error } }) => (
          <FormControl fullWidth className='mt-6'>
            <label id='product-category-label' className={FIELD_LABEL_CLASSES}>
              Product Category <span className='text-red-600'>*</span>
            </label>
            <Select
              {...field}
              {...SELECT_STYLING_PROPS}
              labelId='product-category-label'
              onChange={(event) => {
                setValue("classification", "")
                setValue("productManager", BLANK_PRODUCT_MANAGER_VALUE)
                field.onChange(event)
              }}
              value={field?.value ?? ""}
              renderValue={(value) => {
                if (!value) {
                  return <span className='text-gray-500'>Select Category</span>
                }

                return categoryList.find((e) => e.categoryId === value)?.name ?? ""
              }}
            >
              {categoryList.map((category) => (
                <MenuItem key={category.categoryId} value={category.categoryId}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
            {error?.message && <p className={FIELD_ERROR_CLASSES}>{error.message}</p>}
          </FormControl>
        )}
      />

      <Controller
        name='classification'
        control={control}
        render={({ field, fieldState: { error } }) => (
          <FormControl fullWidth className='mt-6'>
            <label id='product-classification-label' className={FIELD_LABEL_CLASSES}>
              Product Class <span className='text-red-600'>*</span>
            </label>
            <Select
              {...field}
              {...SELECT_STYLING_PROPS}
              labelId='product-classification-label'
              onChange={(event) => {
                const productManager = categoryClassificationsOptions.find(
                  (e) => e.classificationId === event.target.value
                )?.productManager

                setValue("productManager", {
                  id: productManager?.userId ?? "",
                  name: `${productManager?.firstName ?? ""} ${productManager?.lastName ?? ""}`.trim(),
                })
                field.onChange(event)
              }}
              disabled={!categoryClassificationsOptions.length}
              value={field?.value ?? ""}
              renderValue={(value) => {
                if (!value) {
                  return <span className='text-gray-500'>Select Classification Type</span>
                }

                return categoryClassificationsOptions.find((e) => e.classificationId === value)?.name ?? ""
              }}
            >
              {categoryClassificationsOptions.map((classification) => (
                <MenuItem key={classification.classificationId} value={classification.classificationId}>
                  {classification.name}
                </MenuItem>
              ))}
            </Select>
            {error?.message && <p className={FIELD_ERROR_CLASSES}>{error.message}</p>}
            {shouldDisplayClassificationWarningMessage && (
              <p className={FIELD_ERROR_CLASSES}>The following category does not contain classes</p>
            )}
          </FormControl>
        )}
      />

      <Controller
        name='productManager'
        control={control}
        render={({ field }) => (
          <Box className='mt-6'>
            <label htmlFor='product-manager-field' className={FIELD_LABEL_CLASSES}>
              Product Manager
            </label>
            <TextField
              value={field.value?.name ?? ""}
              {...TEXT_FIELD_STYLING_PROPS}
              disabled
              id='product-manager-field'
            />
          </Box>
        )}
      />

      <Controller
        name='productFormInfo'
        control={control}
        render={() => (
          <SelectComponent
            name='productFormInfo'
            control={control}
            label='Product Form'
            placeholder='Add Product Physical Form'
            required
            renderValue={(value: string | undefined) => {
              if (!value) {
                return <span className='text-gray-500'>Select Product Form</span>
              }
              return productFormLabels[value as keyof typeof ProductFormInfoItem] || value
            }}
          >
            {Object.keys(ProductFormInfoItem).map((productInfo) => (
              <MenuItem key={productInfo} value={productInfo}>
                {productFormLabels[productInfo as keyof typeof ProductFormInfoItem]}
              </MenuItem>
            ))}
          </SelectComponent>
        )}
      />
      <Controller
        name='uomId'
        control={control}
        render={({ field }) => (
          <SelectComponent
            {...field}
            name='uomId'
            control={control}
            label='Product Unit of Measurement (UoM)'
            placeholder='Add UoM'
            required
            ref={unitOfMeasurementListRef}
            renderValue={(value: string | undefined) => {
              if (!value) {
                return <span className='text-gray-500'>Select Unit of Measurement</span>
              }
              const uom = unitOfMeasurementList.find(({ id }) => id === value)
              return uom?.name || "Select Unit of Measurement"
            }}
          >
            {unitOfMeasurementList.map(({ id, name }) => (
              <MenuItem key={id} value={id}>
                {name}
              </MenuItem>
            ))}
          </SelectComponent>
        )}
      />
    </form>
  )
}
